// TrackerBase Reports Data Service - handles requests by trackerbase report views for data and actions (e.g., send the report)

import Assert from "tbAssert";
import angular from "angular";

import EventFilter from "tbClient/models/EventFilter";

import TbAppConfigDataServiceModule from "tbClient/config/tb-app-config/TbAppConfigDataService";
import TrackingCalendarDataServiceModule from "./TrackingCalendarDataService";
import ServerFilterProcessorModule from "./ServerFilterProcessorService";
import CalendarGlobalViewServiceModule from "tbClient/services/CalendarGlobalViewService";

const mLogSvc = new WeakMap();
const mHttpSvc = new WeakMap();
const mTrackingCalendarDataSvc = new WeakMap();
const mServerFilterProcessorSvc = new WeakMap();
const mTbServerBaseUrl = new WeakMap();
const mGlobalViewSvc = new WeakMap();

class TrackerBaseReportsDataService {
    constructor(
        $log,
        $http,
        TbAppConfigDataService,
        TrackingCalendarData,
        ServerFilterProcessor,
        CalendarGlobalViewService
    ) {
        Assert.StrictAssert($log != null, "Assertion Failure: Missing log service dependency");
        Assert.StrictAssert($http != null, "Assertion Failure: Missing HTTP service dependency");
        Assert.StrictAssert(TbAppConfigDataService != null, "Assertion Failure: Missing TrackerBase Application Configuration Data Service Dependency");
        Assert.StrictAssert(TrackingCalendarData != null, "Tracking Calendar Data Service dependency is missing or invalid");
        Assert.StrictAssert(ServerFilterProcessor != null, "Assertion Failure: Missing Server Filter Processor");
        Assert.StrictAssert(CalendarGlobalViewService != null, "Global View Service dependency is missing or invalid");

        mLogSvc.set(this, $log);
        mHttpSvc.set(this, $http);

        const tbServerBaseUrl = TbAppConfigDataService.tbServerBaseUrl;
        mTbServerBaseUrl.set(this, tbServerBaseUrl);
        mTrackingCalendarDataSvc.set(this, TrackingCalendarData);
        mServerFilterProcessorSvc.set(this, ServerFilterProcessor);
        mGlobalViewSvc.set(this, CalendarGlobalViewService);
    }

    // Pass request for events reported during a particular time period to the tracking calendar data service.
    getEventsReportedInRange(reportStart, reportEnd) {
        const logSvc = mLogSvc.get(this);
        const currentGlobalView = mGlobalViewSvc.get(this).getCurrentView();
        const emptyEventFilter = new EventFilter({});
        const reportQueryFilter = emptyEventFilter.dataSourceFilter(currentGlobalView.rule);

        const fetchReportedEventsPromise = mTrackingCalendarDataSvc.get(this)
            .getEventsReportedInRange(
                reportStart,
                reportEnd,
                reportQueryFilter
            ).then((reportedEventsData) => {
            // { officeTypeName: string, reportedEvents: TrackingEvent[] }[]
                if (currentGlobalView == null) {
                    return reportedEventsData;
                }
                return reportedEventsData.map((rptDataItem) => {
                    // This shouldn't be required anymore now that the server handles the query filter.
                    const filteredReportedEventsData = {
                        officeTypeName: rptDataItem.officeTypeName,
                        reportedEvents: rptDataItem.reportedEvents.filter(currentGlobalView.clientRule)
                    };
                    if (rptDataItem.reportedEvents.length !== filteredReportedEventsData.reportedEvents.length) {
                        logSvc.warn(`filter result mismatch: reported events changed following client-side filtering of ${rptDataItem.officeTypeName}`, rptDataItem.reportedEvents, filteredReportedEventsData.reportedEvents);
                    }
                    else {
                        logSvc.info("client-side filtering affected no events.");
                    }
                    return filteredReportedEventsData;
                });
            });

        return fetchReportedEventsPromise;
    }

    // Pass request for events in a date range to the tracking calendar data service
    getEventsInDateRange({
        start: reportStart,
        end: reportEnd,
        eventFilter: eventQueryFilter
    }) {
        // Process eventQueryFilter into a kendo-style data structure (which the calendar service knows how to handle)
        // This gives us the opportunity to mix-in the global view rule as well.
        const currentGlobalView = mGlobalViewSvc.get(this).getCurrentView();
        const eventDataFilter = eventQueryFilter.dataSourceFilter(currentGlobalView.rule);

        return mTrackingCalendarDataSvc.get(this)
            .getEventsInDateRange(
                reportStart,
                reportEnd,
                eventDataFilter
            );
    }

    _attachGlobalViewAsQueryFilterString(reportRequestParams) {
        const currentGlobalView = mGlobalViewSvc.get(this).getCurrentView();
        if (currentGlobalView != null) {
            const emptyEventFilter = new EventFilter({});
            const reportQueryFilter = emptyEventFilter.dataSourceFilter(currentGlobalView.rule); // kendo-style filter structure
            // convert to server query filter string
            return Object.assign({}, reportRequestParams, {
                queryFilter: mServerFilterProcessorSvc.get(this).ProcessFilterForServer(reportQueryFilter, "||")
            });
        }
        return reportRequestParams;
    }

    sendTrackingSummaryReport({
        start: reportStart,
        end: reportEnd,
        timeZone: timeZone
    })
    {
        const logSvc = mLogSvc.get(this);
        const sendTrkSmryReportApiEndpoint = `${mTbServerBaseUrl.get(this)}/api/trackingevents/reportdata/tracked/send`;

        const trackingSummaryReportParameters = {
            reportStart: reportStart,
            reportEnd: reportEnd,
            timeZoneId: timeZone
        };

        const trackingSummaryReportParametersWithFilter = this._attachGlobalViewAsQueryFilterString(trackingSummaryReportParameters);

        return mHttpSvc.get(this).post(
                sendTrkSmryReportApiEndpoint,
                trackingSummaryReportParametersWithFilter)
            .then(() => true)
            .catch((err) => {
                logSvc.error("Error sending tracking summary report.", err);
                throw new Error("Unable to send tracking summary report.");
            });
    }

    sendSomeDaysScheduledEventsReport({
        targetDate: reportTargetDate,
        timeZone: reportTimeZone
    }) {
        const logSvc = mLogSvc.get(this);
        const sendReportApiEndpoint = `${mTbServerBaseUrl.get(this)}/api/trackingevents/reportdata/scheduled/send`;

        const trackingScheduleReportParams = {
            reportDate: reportTargetDate,
            reportTimeZone: reportTimeZone
        };

        const trackingScheduleReportParamsWithFilter = this._attachGlobalViewAsQueryFilterString(trackingScheduleReportParams);

        return mHttpSvc.get(this).post(
                sendReportApiEndpoint,
                trackingScheduleReportParamsWithFilter)
            .then(() => true)
            .catch((err) => {
                logSvc.error("Error sending tracking schedule", err);
                throw new Error("Unable to send tracking schedule report.");
            });
    }
}

TrackerBaseReportsDataService.$inject = [
    "$log",
    "$http",
    "TbAppConfigDataService",
    "TrackingCalendarData",
    "ServerFilterProcessor",
    "CalendarGlobalViewService"
];

export default angular
    .module("trackerbase-app.services.trackerbase-reports-data-service", [
        TbAppConfigDataServiceModule,
        TrackingCalendarDataServiceModule,
        ServerFilterProcessorModule,
        CalendarGlobalViewServiceModule
    ])
    .service("TrackerBaseReportsData", TrackerBaseReportsDataService)
    .name;

// Stuff



//////////////////
// WEBPACK FOOTER
// ./src/services/TrackerbaseReportsDataService.js
// module id = ojAp
// module chunks = 1